import type { RMDXOpts as Opts, RDMDProps as Props, RMDXProps } from '@readme/iso';
import type { RunOpts } from '@readme/markdown/dist/lib/run';

import { run, compile, mdx, mdast, Components } from '@readme/markdown';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import rdmdComponentOverrides from '@core/utils/rdmdComponentOverrides';

import Dehydrated from './Dehydrated';
import './style.scss';
import preprocessLegacyVariables from './utils';

const { TailwindStyle } = Components;

const overrides = Object.entries(rdmdComponentOverrides).reduce((acc, [key, value]) => {
  acc[key] = { default: value };
  return acc;
}, {});

export function exec(body: string, opts: Opts) {
  const customBlocksByExport = { ...opts.components };
  const customBlocks = {};

  Object.entries(opts.components || {}).forEach(([tag, source]) => {
    const code = compile(source, { ...opts, components: {} });
    const mod = run(code, { ...opts, components: overrides } as RunOpts);
    Object.keys(mod).forEach(subTag => {
      if (['toc', 'Toc', 'default', 'stylesheet'].includes(subTag)) return;

      customBlocksByExport[subTag] = source;
    });
    customBlocks[tag] = mod;
  });

  const components = { ...overrides, ...customBlocks };

  let content = body;

  if (opts.format === 'md') {
    // Pre-process the body for Variable and Glossary replacements
    content = preprocessLegacyVariables(body, opts.variables);
  }

  const vfile = compile(content, { useTailwind: true, ...opts, components: customBlocksByExport });
  const module = run(vfile, { ...opts, components } as RunOpts);

  return module;
}

function useRMDX(body: string = '', _, optsParam: Opts) {
  if (optsParam.format === 'md' && optsParam.components) {
    // delete components from opts when rendering plain markdown,
    // so we don't try to sanitize mdx within the custom blocks
    delete optsParam.components;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const opts = useMemo(() => optsParam, [JSON.stringify(optsParam)]);

  return useMemo(() => exec(body, opts), [body, opts]);
}

export const TOC = ({ body, children, opts = {} }: Props) => {
  const { t } = useTranslation();
  const Content = useRMDX((body || children) as string, true, { useTailwind: false, ...opts });
  return Content ? <Content.Toc heading={t('tableOfContents')} /> : null;
};

const RMDX = ({
  body,
  children = body,
  className,
  css,
  dehydrated,
  excerpt,
  opts = {},
  skipBaseClassName = false,
  Tag = 'div',
  darkModeDataAttribute = undefined,
  ...rest
}: RMDXProps) => {
  let doc: string = children as string;

  // If we're rendering an excerpt
  if (excerpt && children) {
    // generate the mdast
    const ast = mdast(children);
    /**
     * If the mdast doesn't have length on its children field (aka: there's no body content),
     * fallback to an empty string as `rmdx.mdx` only accepts children of type RootContent and there's nothing to parse
     *  */
    doc = ast.children.length ? mdx({ type: 'root', children: [ast.children[0]] }) : '';
  }

  const Content = useRMDX(doc, false, opts);
  const classes = [
    skipBaseClassName !== true && 'markdown-body',
    className || '',
    opts.useTailwindRoot && 'readme-tailwind',
  ];
  const props = { ...rest, className: `rm-Markdown ${classes.filter(c => c).join(' ')}`, 'data-testid': 'RDMD' };
  const [useAppDataAttribute, setUseAppDataAttribute] = useState(!darkModeDataAttribute);

  useEffect(() => {
    if (darkModeDataAttribute) return;

    const appEl = document.querySelector('[data-color-mode]');
    setUseAppDataAttribute(['light', 'dark'].includes(appEl?.getAttribute('data-color-mode') || ''));

    /*
     * @note: This is to support dark mode in tailwind styles. In the preview
     * panel, we want to be able to set it to based on a toggle, independent of
     * the rest of the pages scheming. So we can simple use the data attribute
     * selector.
     *
     * I'm maybe not savvy enough to figure out how to combine that with a
     * media query for tailwind, so we check if the main app is using system
     * settings (when `data-color-mode` === `null` or `'system'`). Or if it's
     * been set, we switch to using the data attribute.
     */
    const observer = new MutationObserver((records: MutationRecord[]) => {
      records.forEach(record => {
        if (!(record.target instanceof HTMLElement)) return;

        const { dataset } = record.target;
        setUseAppDataAttribute(['dark', 'light'].includes(dataset.colorMode || ''));
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-mode'],
    });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [darkModeDataAttribute]);

  if (!Content && dehydrated) {
    return <Dehydrated {...props} string={dehydrated} Tag={Tag} />;
  }

  return Content ? (
    <Tag {...props}>
      {/* @todo: figure out how to clean this up */}
      {!!css && <style suppressHydrationWarning>{css}</style>}

      {opts.format === 'md' ? (
        <Content.default />
      ) : (
        <TailwindStyle darkModeDataAttribute={useAppDataAttribute ? 'data-color-mode' : darkModeDataAttribute}>
          <Content.default />
        </TailwindStyle>
      )}
    </Tag>
  ) : null;
};

export default RMDX;
